'use client'

import { useRef, useState, useCallback, useEffect } from 'react'
import { useTranslations } from 'next-intl'

const ITEM_COUNT = 6

export function VisionCarousel() {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const t = useTranslations('appleVisionPro.visionCarousel')

  const features = Array.from({ length: ITEM_COUNT }, (_, i) => ({
    tag: t(`items.${i}.tag` as `items.0.tag`),
    title: t(`items.${i}.title` as `items.0.title`),
    description: t(`items.${i}.description` as `items.0.description`),
  }))

  const total = features.length

  const syncIndex = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const spacer = el.firstElementChild as HTMLElement | null
    const paddingLeft = spacer?.offsetWidth ?? 0
    const cards = Array.from(el.querySelectorAll('[data-card]')) as HTMLElement[]
    let closest = 0
    let minDist = Infinity
    cards.forEach((card, i) => {
      const dist = Math.abs(card.offsetLeft - paddingLeft - el.scrollLeft)
      if (dist < minDist) { minDist = dist; closest = i }
    })
    setActiveIndex(closest)
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.addEventListener('scroll', syncIndex, { passive: true })
    return () => el.removeEventListener('scroll', syncIndex)
  }, [syncIndex])

  const scrollToCard = (index: number) => {
    const el = scrollRef.current
    if (!el) return
    const spacer = el.firstElementChild as HTMLElement | null
    const paddingLeft = spacer?.offsetWidth ?? 0
    const cards = el.querySelectorAll('[data-card]')
    const card = cards[index] as HTMLElement | undefined
    if (!card) return
    const target = Math.min(card.offsetLeft - paddingLeft, el.scrollWidth - el.clientWidth)
    el.scrollTo({ left: target, behavior: 'smooth' })
  }

  const handlePrev = () => scrollToCard(Math.max(activeIndex - 1, 0))
  const handleNext = () => scrollToCard(Math.min(activeIndex + 1, total - 1))

  return (
    <div className="relative">
      {/* Scroll track */}
      <div
        ref={scrollRef}
        className="flex gap-6 overflow-x-auto scroll-smooth pb-6"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div className="flex-shrink-0 w-4 sm:w-8 lg:w-16" aria-hidden />

        {features.map((feature, i) => (
          <div
            key={i}
            data-card
            className="flex-shrink-0 w-[90vw] sm:w-[65vw] lg:w-[420px] rounded-3xl border border-white/10 hover:border-strandforge-purple/40 transition-all duration-300 bg-black/30 backdrop-blur-sm p-8 flex flex-col"
          >
            <span className="text-xs uppercase tracking-widest font-semibold mb-3 text-strandforge-purple-light">
              {feature.tag}
            </span>
            <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
            <p className="text-gray-400 leading-relaxed">{feature.description}</p>
          </div>
        ))}

        <div className="flex-shrink-0 w-[calc(10vw-16px)] sm:w-[calc(35vw-32px)] lg:w-[calc(100vw-484px)]" aria-hidden />
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-6 mt-4 px-4">
        <button
          onClick={handlePrev}
          disabled={activeIndex === 0}
          aria-label="Previous"
          className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white disabled:opacity-25 disabled:pointer-events-none hover:border-strandforge-purple/60 hover:bg-strandforge-purple/10 transition-all duration-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="flex gap-2">
          {features.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollToCard(i)}
              aria-label={`Go to card ${i + 1}`}
              className="h-2 rounded-full p-0 border-0 outline-none transition-[width,background-color] duration-300"
              style={{
                width: i === activeIndex ? '2rem' : '0.5rem',
                backgroundColor: i === activeIndex ? '#7E34DC' : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={activeIndex === total - 1}
          aria-label="Next"
          className="w-12 h-12 rounded-full border border-white/20 flex items-center justify-center text-white disabled:opacity-25 disabled:pointer-events-none hover:border-strandforge-purple/60 hover:bg-strandforge-purple/10 transition-all duration-200"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </button>
      </div>
    </div>
  )
}
